/**
 * API layer for the Slide Sense feature (user-facing endpoints).
 *
 * Uses the shared Axios client from @/lib/api-client which handles
 * authentication, base URL, and response unwrapping.
 */

import type {
  SlideSenseCollection,
  SlideSenseDeck,
  SlideSenseFolder,
  SlideSenseSlide,
} from "./types"

import { api } from "@/lib/api-client"

// ---------------------------------------------------------------------------
// Request / Response types
// ---------------------------------------------------------------------------

export interface FetchSlidesParams {
  folder_id?: string
  deck_id?: string
  page?: number
  page_size?: number
  /** Multi-folder scope (comma-joined server-side); overrides folder_id. */
  folder_ids?: string
  /** Filter results to slides that appear in at least one of these collections. */
  collection_ids?: string
}

export interface SearchSlidesParams {
  q: string
  folder_id?: string
  category?: string
  /** Multi-folder scope (comma-joined server-side); overrides folder_id when set. */
  folder_ids?: string
  /** Filter results to slides that appear in at least one of these collections. */
  collection_ids?: string
}

/**
 * Which presenter modes an admin has made available for this tenant.
 * Both default to `true` (see backend module_config.py) so absent config
 * preserves the historical "everything available" behavior.
 */
export interface SlideSensePresenterModes {
  /** Audience-only Presentation Mode (single-screen, F5 equivalent). */
  presentation: boolean
  /** Dual-window present with the separate Presenter View console. */
  presenter_view: boolean
}

export interface SlideSenseModuleConfig {
  presenter_modes: SlideSensePresenterModes
}

// ---------------------------------------------------------------------------
// API functions
// ---------------------------------------------------------------------------

/**
 * QUERY: Fetch all folders available to the current user.
 */
export function fetchFolders(): Promise<SlideSenseFolder[]> {
  return api.get("/slide-sense/folders")
}

/**
 * QUERY: Fetch the tenant's Slide Sense module config (presenter-mode
 * availability). Used to gate Present UI; defaults are enabled server-side.
 */
export function fetchModuleConfig(): Promise<SlideSenseModuleConfig> {
  return api.get("/slide-sense/config")
}

/**
 * QUERY: Fetch decks in a folder (descendants included when folder_id is set).
 * User-facing endpoint — mirrors the admin one, but returns the public deck
 * shape for any authenticated user.
 */
export function fetchDecks(params: { folder_id?: string }): Promise<SlideSenseDeck[]> {
  return api.get("/slide-sense/decks", { params })
}

/**
 * QUERY: Fetch cover slide IDs for the given decks (deck_id -> first_slide_id).
 * Scoped to the decks the caller is displaying so the backend doesn't resolve
 * covers for the whole tenant. Lets the landing render real deck thumbnails
 * via SlideThumbnailImage instead of a placeholder icon.
 */
export function fetchDeckCovers(deckIds: string[]): Promise<Record<string, string>> {
  return api.get("/slide-sense/decks/covers", { params: { deck_ids: deckIds.join(",") } })
}

/**
 * QUERY: Fetch slides with optional filtering and pagination.
 */
export function fetchSlides(params: FetchSlidesParams): Promise<SlideSenseSlide[]> {
  return api.get("/slide-sense/slides", { params })
}

export interface SpeakerNotesResponse {
  speaker_notes: string
}

/**
 * QUERY: Fetch real PPTX speaker notes for one slide.
 *
 * When Cosmos has no stored notes (legacy ingest), the backend extracts
 * them from the single-slide PPTX blob on demand and backfills Cosmos.
 */
export function fetchSpeakerNotes(slideId: string): Promise<SpeakerNotesResponse> {
  return api.get(`/slide-sense/slides/${slideId}/speaker-notes`)
}

/** Backend caps page_size at 200. Walks pages until every slide in the deck is loaded. */
export async function fetchAllDeckSlides(deckId: string): Promise<SlideSenseSlide[]> {
  const PAGE_SIZE = 200
  const all: SlideSenseSlide[] = []
  let page = 0
  let hasMore = true
  while (hasMore) {
    const batch = await fetchSlides({ deck_id: deckId, page, page_size: PAGE_SIZE })
    all.push(...batch)
    hasMore = batch.length === PAGE_SIZE
    page += 1
  }
  return all
}

/**
 * QUERY: Search slides by text query with optional folder and category filters.
 */
export function searchSlides(params: SearchSlidesParams): Promise<SlideSenseSlide[]> {
  return api.get("/slide-sense/slides/search", { params })
}

/**
 * Thrown when /slide-sense/merge returns 422 (one or more selected slides
 * have no PPTX blob) or 502 (a download with a real path failed). The
 * caller should surface a dialog so the user can remove the bad slides
 * from the builder instead of silently shipping a corrupted PPTX.
 */
export class MergeUnmergeableError extends Error {
  readonly slideIds: string[]
  readonly slideTitles: string[]
  readonly status: number

  constructor(params: {
    message: string
    slideIds: string[]
    slideTitles: string[]
    status: number
  }) {
    super(params.message)
    this.name = "MergeUnmergeableError"
    this.slideIds = params.slideIds
    this.slideTitles = params.slideTitles
    this.status = params.status
  }
}

interface UnmergeablePayload {
  error?: string
  message?: string
  slide_ids?: string[]
  slide_titles?: string[]
}

async function readBlobAsJson(blob: Blob): Promise<UnmergeablePayload | null> {
  try {
    const text = await blob.text()
    return JSON.parse(text) as UnmergeablePayload
  } catch {
    return null
  }
}

/**
 * MUTATION: Merge selected slides into a single .pptx file.
 *
 * Returns the file as a Blob for client-side download.
 *
 * Throws MergeUnmergeableError when the backend rejects the merge
 * because at least one slide is unrecoverable (status 422 / 502).
 */
export async function mergeSlides(slideIds: string[], filename: string): Promise<Blob> {
  try {
    const response = await api.post(
      "/slide-sense/merge",
      { slide_ids: slideIds, filename },
      { responseType: "blob" }
    )

    // The Axios interceptor unwraps response.data automatically, but for blob
    // responses we may get the raw data directly.
    if (response instanceof Blob) {
      return response
    }

    // Fallback: if the interceptor returned the AxiosResponse, extract the blob.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const raw = response as any
    if (raw?.data instanceof Blob) {
      return raw.data
    }

    throw new Error("Unexpected response format from merge endpoint")
  } catch (err) {
    // Axios rejects on 4xx/5xx. With responseType: "blob" the error body
    // also comes back as a Blob, so we have to read it as text and parse.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const anyErr = err as any
    const status = anyErr?.response?.status
    const data = anyErr?.response?.data
    if ((status === 422 || status === 502) && data instanceof Blob) {
      const payload = await readBlobAsJson(data)
      if (payload?.error === "unmergeable_slides") {
        throw new MergeUnmergeableError({
          message: payload.message ?? "Some slides cannot be merged",
          slideIds: payload.slide_ids ?? [],
          slideTitles: payload.slide_titles ?? [],
          status,
        })
      }
    }
    throw err
  }
}

// ---------------------------------------------------------------------------
// Batch thumbnail fetch
// ---------------------------------------------------------------------------

export interface BatchThumbnail {
  slide_id: string
  content_type: string
  data_b64: string
}

export interface BatchThumbnailsResponse {
  thumbnails: BatchThumbnail[]
  missing: string[]
}

/**
 * MUTATION: Fetch many slide thumbnails in one round-trip.
 *
 * Returns thumbnails inline as base64 plus a list of slide IDs that
 * could not be served (other tenant, render_failed, or unknown).
 * Capped at 100 IDs per call by the backend.
 */
export function fetchThumbnailsBatch(slideIds: string[]): Promise<BatchThumbnailsResponse> {
  return api.post("/slide-sense/thumbnails/batch", { slide_ids: slideIds })
}

// ---------------------------------------------------------------------------
// AI Search API
// ---------------------------------------------------------------------------

export interface AiSearchResult extends SlideSenseSlide {
  ai_search_reason?: string
}

export interface AiSearchResponse {
  results: AiSearchResult[]
  summary: string
}

/**
 * MUTATION: AI-powered semantic slide search.
 *
 * Scope precedence on the backend: `folderIds` (multi) wins over `folderId`
 * (legacy single). `collectionIds` further intersects the result set with
 * slide memberships of those saved presentations.
 */
export function aiSearchSlides(
  query: string,
  options: { folderId?: string; folderIds?: string[]; collectionIds?: string[] } = {}
): Promise<AiSearchResponse> {
  return api.post("/slide-sense/slides/ai-search", {
    query,
    folder_id: options.folderId ?? null,
    folder_ids: options.folderIds && options.folderIds.length > 0 ? options.folderIds : null,
    collection_ids:
      options.collectionIds && options.collectionIds.length > 0 ? options.collectionIds : null,
  })
}

// ---------------------------------------------------------------------------
// Collections API
// ---------------------------------------------------------------------------

/**
 * QUERY: Fetch all saved collections for the current user.
 */
export function fetchCollections(): Promise<SlideSenseCollection[]> {
  return api.get("/slide-sense/collections")
}

/**
 * MUTATION: Create a new collection from selected slide IDs.
 */
export function createCollection(data: {
  name: string
  slide_ids: string[]
}): Promise<SlideSenseCollection> {
  return api.post("/slide-sense/collections", data)
}

/**
 * QUERY: Fetch a single collection by ID (includes populated slides).
 */
export function fetchCollection(id: string): Promise<SlideSenseCollection> {
  return api.get(`/slide-sense/collections/${id}`)
}

/**
 * MUTATION: Update a collection's name or slide IDs.
 */
export function updateCollection(
  id: string,
  data: { name?: string; slide_ids?: string[] }
): Promise<SlideSenseCollection> {
  return api.put(`/slide-sense/collections/${id}`, data)
}

/**
 * MUTATION: Delete a collection.
 */
export function deleteCollection(id: string): Promise<{ deleted: boolean }> {
  return api.delete(`/slide-sense/collections/${id}`)
}

/**
 * MUTATION: Clear the removed_slides[] notification on a collection.
 */
export function acknowledgeRemovals(id: string): Promise<{ ok: boolean }> {
  return api.post(`/slide-sense/collections/${id}/acknowledge-removals`)
}
